import { Link } from "react-router-dom";
import type { Icon } from "@phosphor-icons/react";
import { Megaphone, NotePencil, PushPin, Shield, User } from "@phosphor-icons/react";
import { MetricCard, PageHeader } from "@breakingthelines/design-system";

import { useModerationPendingCount } from "@/hooks/use-moderation-pending-count";
import { useTotalContentCount, useTotalUsersCount } from "@/hooks/use-home-counts";
import { formatDeltaChange, formatDeltaSince, useHomeDeltas } from "@/hooks/use-home-deltas";
import type { HomeMetricName } from "@/lib/admin-api";

import styles from "./dashboard-home.module.css";

type HomeCard = {
  label: string;
  to: string;
  icon: Icon;
};

type HomeMetric = {
  label: string;
  value: number;
  metric: HomeMetricName;
  /**
   * Which direction is good news. False for the moderation queue: a queue that
   * grew by nine is not a nine-point win, and the design system colours the
   * delta accordingly.
   */
  higherIsBetter: boolean;
};

/**
 * Home is a launcher with a readout on top: the counts that are cheap to fetch
 * (open moderation flags, total content, total users - see
 * use-moderation-pending-count.ts and use-home-counts.ts), each with its
 * seven-day movement from admin-service's daily snapshots (use-home-deltas.ts),
 * then a card per working area.
 *
 * The numbers sit in their own row rather than inside the launcher tiles. A
 * MetricCard is a number with a movement under it and a tile is an icon with a
 * destination; mixing three of one and two of the other into a single grid gives
 * neither shape.
 *
 * Every number here is absent rather than approximated. A count that failed
 * drops its whole card, and a metric with no snapshot history drops its delta
 * line - the seven-day movement cannot exist until seven days of snapshots do,
 * and a zero in its place would read as "no change this week".
 *
 * Campaigns, Analytics, Audit and Pages are stub areas owned by a parallel
 * unit - they intentionally have no card here.
 */
export function DashboardHomePage() {
  const openFlags = useModerationPendingCount();
  const totalUsers = useTotalUsersCount();
  const totalContent = useTotalContentCount();
  const deltas = useHomeDeltas();

  const metrics: HomeMetric[] = [];
  if (openFlags !== undefined) {
    metrics.push({
      label: "Open flags",
      value: openFlags,
      metric: "moderation.pending",
      higherIsBetter: false,
    });
  }
  if (totalContent !== undefined) {
    metrics.push({
      label: "Total content",
      value: totalContent,
      metric: "content.total",
      higherIsBetter: true,
    });
  }
  if (totalUsers !== undefined) {
    metrics.push({
      label: "Total users",
      value: totalUsers,
      metric: "users.total",
      higherIsBetter: true,
    });
  }

  const anyDelta = metrics.some((metric) => deltas[metric.metric] !== undefined);

  const cards: HomeCard[] = [
    { label: "Moderation", to: "/moderation/queue", icon: Shield },
    { label: "Discovery", to: "/search/editorial-overrides", icon: PushPin },
    { label: "Content", to: "/content", icon: NotePencil },
    { label: "Users & Access", to: "/users", icon: User },
    { label: "Advertisers", to: "/advertisers", icon: Megaphone },
  ];

  return (
    <div className={styles.page}>
      <PageHeader title="Home" kicker="BTL Admin" />

      {metrics.length > 0 ? (
        <section className={styles.metrics}>
          {metrics.map((metric) => {
            const delta = deltas[metric.metric];
            return (
              <MetricCard
                key={metric.metric}
                label={metric.label}
                value={metric.value.toLocaleString()}
                higherIsBetter={metric.higherIsBetter}
                delta={delta ? formatDeltaChange(delta.change) : undefined}
                hint={delta ? formatDeltaSince(delta.since) : undefined}
              />
            );
          })}
        </section>
      ) : null}

      {metrics.length > 0 && !anyDelta ? (
        <p className={styles.deltaNote}>Seven-day movement appears after a week of history.</p>
      ) : null}

      <div className={styles.cardGrid}>
        {cards.map((card) => {
          const CardIcon = card.icon;
          return (
            <Link key={card.label} to={card.to} className={styles.card}>
              <CardIcon size={20} className={styles.cardIcon} />
              <span className={styles.cardLabel}>{card.label}</span>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
